import { images } from '../../images';
import {
  IClient,
  IService,
  ITown,
  IUser,
  IUserRegular,
} from '../models';
import {
  CommunicationUserType,
  DriverSuggestionType,
  IncidenceAffair,
  SuggestionVisibility,
  InputType,
  ObjectFoundAffair,
  ObjectLostAffair,
  ObservationAffair,
  UserRole,
  UserSortType,
  UserStatus,
  UserType,
} from '../models/enums';
import { SuggestionPriority } from '../models/enums/suggestionPriority';
import { SuggestionStatus } from '../models/enums/suggestionStatus';
import { SuggestionType } from '../models/enums/suggestionType';
import { ISuggestion } from '../models/suggestion';
import {
  ClientsService,
  StorageService,
  TripsService,
  UsersService,
} from '../services';
import { LiteralService } from '../services/literal/literal.service';
import { ServiceInfoService } from '../services/service-info/service-info.service';
import { SuggestionAffair } from '../models/enums/suggestionAffair';
import { ComplaintAffair } from '../models/enums/complaintAffair';
import { TooltipPosition } from '../shared/tooltip/tooltip-position';
import { formData, IExpeditionSuggestionData, ISuggestionFormOptions } from './suggestionData';
import {
  getExpeditionOptions,
  suggestionExpeditionOption,
} from './expeditionOptions';
import {
  SUGGESTION_VISIBILITIES,
  isVisibleToPassengers,
  suggestionExpeditionLabel,
  suggestionVisibilityIcon,
  suggestionWithoutSuggesterLiteral,
  toSuggestionExpedition,
} from '../utils/suggestionTickets';

export class UserSuggestionData {
  showMoreUsers: boolean;
  search?: boolean;
  value?: string;
  clientChanged?: boolean;

  constructor(data: Partial<UserSuggestionData>) {
    Object.assign(this, data);
  }
}

let expeditions: any[] = [];
let expeditionsServiceId: number | undefined;
let expeditionsPage = 1;
let expeditionsSearch = '';

export async function loadSuggestionExpeditions(
  tripsService: TripsService,
  suggestion: ISuggestion,
  expeditionData?: IExpeditionSuggestionData,
) {
  const serviceId = suggestion.service!.id!;
  const isNewQuery = !expeditionData?.showMore || expeditionsServiceId !== serviceId;
  if (isNewQuery) {
    expeditionsPage = 1;
    expeditionsSearch = expeditionData?.search ?? '';
  } else {
    expeditionsPage += 1;
  }
  const selectedExpedition = suggestion.expedition ?? undefined;
  const newOptions = await getExpeditionOptions(
    tripsService,
    {
      services: [{ id: serviceId }],
      page: expeditionsPage,
      search: expeditionsSearch || undefined,
      excludedIds: selectedExpedition ? [selectedExpedition.id] : [],
    },
    (trip) => suggestionExpeditionOption(toSuggestionExpedition(trip)),
  );
  expeditions = isNewQuery
    ? [...(selectedExpedition ? [suggestionExpeditionOption(selectedExpedition)] : []), ...newOptions]
    : [...expeditions, ...newOptions];
  expeditionsServiceId = serviceId;
}

export async function getClients(usersService: UsersService) {
  return await usersService.getPossibleClientTowns().then((res) => {
    clients = [];
    towns = [];
    res.clients.map((client: IClient) => {
      clients.push({
        id: client.id,
        value: client,
        label: client.name,
        name: client.name,
      });
    });
  });
}

export async function getTownsByClient(usersService: UsersService, clientId: any, selected?: any[]) {
  return await usersService.getTownsByClient(clientId).then((res: any) => {
    towns = [];
    townsClientId = clientId;
    res.map((town: ITown) => {
      towns.push({ id: town.id, value: town, label: town.name });
    });
  });
}

export async function getServices(
  clientService: ClientsService,
  clientId: number,
) {
  return await clientService.getServices(clientId).then((res) => {
    const serviceInfos: any[] = [];
    res.map((service: IService) => {
      serviceInfos.push({
        id: service.id,
        value: service,
        label: service.name ? `${service.tag}: ${service.name}` : service.tag,
      });
    });

    return { serviceInfos: serviceInfos };
  });
}

export async function getServicesByTown(
  serviceInfoService: ServiceInfoService,
  townId: number,
) {
  return await serviceInfoService.getServiceInfosSearch(100, 1, [{ id: townId } as ITown]).then((res: any) => {
    const content = res.content || res;
    serviceInfos = [];
    (content as any[]).map((service: IService) => {
      serviceInfos.push({
        id: service.id,
        value: service,
        label: service.name ? `${service.tag}: ${service.name}` : service.tag,
      });
    });
  });
}

export async function prepareSuggestionClient(
  usersService: UsersService,
  suggestion: ISuggestion,
  creating?: boolean,
): Promise<number> {
  if (clients.length === 0) await getClients(usersService);
  if (clients.length === 1) suggestion.client ??= clients[0].value;

  if (creating && !suggestion.client) {
    const suggesterClientIds = (suggestion.userSuggester?.clients ?? []).map((client) => client.id);
    const candidateClients = clients.filter((client) => suggesterClientIds.includes(client.id));
    if (candidateClients.length === 1) suggestion.client = candidateClients[0].value;
  }
  if (suggestion.client?.id && !suggestion.client.name) {
    suggestion.client = clients.find((client) => client.id === suggestion.client!.id)?.value ?? suggestion.client;
  }
  return clients.length === 1 ? 2 : 3;
}

let clients: any[] = [];
let towns: any[] = [];
let townsClientId: number | undefined;
let serviceInfos: any[] = [];
let serviceInfosTownId: number | undefined;
let users: any[] = [];

export async function getSuggestionForm(
  suggestion: ISuggestion = {} as ISuggestion,
  userType: CommunicationUserType,
  literalService: LiteralService,
  usersService: UsersService,
  storageService: StorageService,
  clientsService: ClientsService,
  userData?: UserSuggestionData,
  changes?: any,
  editing?: boolean,
  creating?: boolean,
  currentStep?: number,
  serviceInfoService?: ServiceInfoService,
  formOptions: ISuggestionFormOptions = {},
): Promise<any> {
  const { tripsService, expeditionData } = formOptions;
  const adminRole = await storageService.getRole();
  const suggesterUserType = userType === CommunicationUserType.DRIVERS ? UserType.Driver : UserType.Regular;

  let statuses: any[] = [];
  let affairs: any[] = [];
  let suggestionAffairs: any[] = [];
  let complaintAffairs: any[] = [];
  let observationAffairs: any[] = [];
  let incidenceAffairs: any[] = [];
  let objectLostAffairs: any[] = [];
  let objectFoundAffairs: any[] = [];
  let types: any[] = [];
  let priorities: any[] = [];

  const lastStep = await prepareSuggestionClient(usersService, suggestion, creating);
  const hideClientsStep = lastStep === 2;
  const onLocationStep = !!creating && currentStep === lastStep - 1;
  const expeditionEditable = creating ? onLocationStep : !!editing;

  if (clients.length > 1 && currentStep === 1) towns = [];
  else if (onLocationStep && suggestion.client?.id && (towns.length === 0 || townsClientId !== suggestion.client.id)) {
    await getTownsByClient(usersService, suggestion.client.id).then(() => {
      if (towns.length === 1) suggestion.town = towns[0].value;
    });
  }
  if (suggestion.town?.id) suggestion.town = towns.find((town) => town.id === suggestion.town!.id)?.value ?? suggestion.town;

  if (userData && onLocationStep) {
    if (userData.showMoreUsers) {
      page += 1;
      pageSearched += 1;
    } else {
      users = [];
      page = 0;
      usersSearched = [];
      pageSearched = 0;
    }
    await loadUsers(
      usersService,
      suggesterUserType,
      userData.search ? usersSearched : users,
      userData.search ? pageSearched : page,
      suggestion.client?.id,
      suggestion.userSuggester,
      userData.search ? userData.value : undefined,
    );
  } else if (suggestion.userSuggester?.id) {
    addSelectedUser(users, suggestion.userSuggester as IUserRegular);
  }

  if (
    onLocationStep &&
    suggestion.town?.id &&
    serviceInfoService &&
    (serviceInfos == undefined || serviceInfos.length == 0 || serviceInfosTownId !== suggestion.town.id)
  ) {
    serviceInfosTownId = suggestion.town.id;
    await getServicesByTown(serviceInfoService, suggestion.town.id);
  }
  if (suggestion.service?.id) {
    suggestion.service = serviceInfos.find((serviceInfo) => serviceInfo.id === suggestion.service!.id)?.value ?? suggestion.service;
  }
  if (!suggestion.service?.id) {
    expeditions = [];
    expeditionsServiceId = undefined;
  } else if (tripsService && expeditionEditable && (expeditionData || expeditionsServiceId !== suggestion.service.id)) {
    await loadSuggestionExpeditions(tripsService, suggestion, expeditionData);
  }

  if (!suggestion.visibility) suggestion.visibility = SuggestionVisibility.PRIVATE;
  const visibilities = SUGGESTION_VISIBILITIES.map((visibility) => ({
    id: visibility,
    value: visibility,
    label: `suggestions.visibilities.${visibility}`,
    data: {
      icon: suggestionVisibilityIcon(visibility),
      description: `suggestions.visibilityTooltips.${visibility}`,
    },
  }));
  const publicMessageRequired = isVisibleToPassengers(suggestion.visibility);

  Object.values(SuggestionStatus).forEach((status: SuggestionStatus) => {
    statuses.push({
      id: status,
      value: status,
      label: `suggestions.statuses.${status.toLowerCase()}`,
    });
  });
  Object.values(SuggestionPriority).forEach((priority: SuggestionPriority) => {
    priorities.push({
      id: priority,
      value: priority,
      label: `suggestions.priorities.${priority.toLowerCase()}`,
    });
  });
  if (userType === CommunicationUserType.USERS) {
    Object.values(SuggestionType).forEach((type: SuggestionType) => {
      types.push({
        id: type,
        value: type,
        label: `suggestions.${type.toLowerCase()}`,
      });
    });
    Object.values(SuggestionAffair).forEach((suggestionAffair: SuggestionAffair) => {
      suggestionAffairs.push({
        id: suggestionAffair,
        value: suggestionAffair,
        label: `suggestions.affairs.${suggestionAffair}`,
      });
    });
    Object.values(ComplaintAffair).forEach((complaintAffair: ComplaintAffair) => {
      complaintAffairs.push({
        id: complaintAffair,
        value: complaintAffair,
        label: `suggestions.affairs.${complaintAffair}`,
      });
    });
    Object.values(ObjectLostAffair).forEach((objectLostAffair: ObjectLostAffair) => {
      objectLostAffairs.push({
        id: objectLostAffair,
        value: objectLostAffair,
        label: `suggestions.affairs.${objectLostAffair}`,
      });
    });
  } else {
    Object.values(DriverSuggestionType).forEach((type: DriverSuggestionType) => {
      types.push({
        id: type,
        value: type,
        label: `suggestions.${type.toLowerCase()}`,
      });
    });
    Object.values(ObservationAffair).forEach((observationAffair: ObservationAffair) => {
      observationAffairs.push({
        id: observationAffair,
        value: observationAffair,
        label: `suggestions.affairs.${observationAffair}`,
      });
    });
    Object.values(IncidenceAffair).forEach((incidencesAffair: IncidenceAffair) => {
      incidenceAffairs.push({
        id: incidencesAffair,
        value: incidencesAffair,
        label: `suggestions.affairs.${incidencesAffair}`,
      });
    });
    Object.values(ObjectFoundAffair).forEach((objectFoundAffair: ObjectFoundAffair) => {
      objectFoundAffairs.push({
        id: objectFoundAffair,
        value: objectFoundAffair,
        label: `suggestions.affairs.${objectFoundAffair}`,
      });
    });
  }


  switch(suggestion.type) {
    case SuggestionType.COMPLAINT:
      affairs = complaintAffairs;
      break;
    case SuggestionType.OBJECT_LOST:
      affairs = objectLostAffairs;
      break;
    case DriverSuggestionType.OBSERVATION:
      affairs = observationAffairs;
      break;
    case DriverSuggestionType.INCIDENCE:
      affairs = incidenceAffairs;
      break;
    case DriverSuggestionType.OBJECT_FOUND:
      affairs = objectFoundAffairs;
      break;
    case SuggestionType.SUGGESTION:
    default:
      affairs = suggestionAffairs;
      break;
  }
  const isObjectLostType = suggestion.type === SuggestionType.OBJECT_LOST;
  const isObjectFoundType = suggestion.type === DriverSuggestionType.OBJECT_FOUND;
  if (isObjectLostType) {
    suggestion.affair = ObjectLostAffair.OBJECT_LOST;
  } else if (isObjectFoundType) {
    suggestion.affair = ObjectFoundAffair.OBJECT_FOUND;
  } else if (creating && suggestion.affair && !affairs.some((affair) => affair.value === suggestion.affair)) {
    suggestion.affair = affairs[0].value;
  }
  
  const expeditionField: formData = {
    id: 'expedition' + (suggestion.id ? `-${suggestion.id}` : ''),
    img: images.suggestions.expedition,
    title: 'suggestions.data.expedition',
    value: expeditionEditable ? suggestion.expedition : suggestionExpeditionLabel(suggestion.expedition) || '-',
    translateValue: false,
    array: false,
    step: lastStep - 1,
    color: true,
    expanded: !creating,
    hidden: creating && currentStep! < (lastStep - 1),
    edit: {
      editable: expeditionEditable,
      inputType: InputType.INFINITE_SCROLL,
      customSearchEnabled: true,
      required: false,
      disabled: !suggestion.service?.id,
      values: expeditions,
      valueToChange: 'expedition',
      placeholder: literalService.get(suggestion.service?.id ? 'suggestions.data.expeditionPlaceholder' : 'suggestions.data.expeditionRequiresService', true),
    }
  };

  const data: formData[] = [
    {
      id: 'client' + (suggestion.id ? `-${suggestion.id}` : ''),
      img: images.suggestions.clients,
      title: 'suggestions.data.client',
      value: creating && currentStep! === 1 ? suggestion.client : suggestion.client?.name,
      translateValue: true,
      color: true,
      step: 1,
      expanded: false,
      hidden:
        !creating ||
        hideClientsStep,
      edit: {
        editable: creating && currentStep! === 1 ? true : false,
        required: true,
        inputType: InputType.SELECT_SEARCH,
        values: clients,
        valueToChange: 'client',
      }
    },
    {
      id: 'area' + (suggestion.id ? `-${suggestion.id}` : ''),
      img: images.area,
      title: 'suggestions.data.town',
      value: creating && currentStep! === (lastStep - 1) ? suggestion.town : suggestion.town?.name,
      translateValue: true,
      array: false,
      step: lastStep - 1,
      color: true,
      expanded: false,
      hidden: !creating || currentStep! < (lastStep - 1),
      edit: {
        editable: creating && currentStep! === (lastStep - 1) ? true : false,
        inputType: InputType.SELECT_SEARCH,
        required: true,
        disabled: towns.length === 0,
        values: towns,
        valueToChange: 'town',
      }
    },
    {
      id: 'service' + (suggestion.id ? `-${suggestion.id}` : ''),
      img: images.suggestions.services,
      title: 'suggestions.data.service',
      value: creating && currentStep! === (lastStep - 1) ? suggestion.service : suggestion.service ? (suggestion.service.name ? `${suggestion.service.tag}: ${suggestion.service.name}` : suggestion.service.tag) : '',
      translateValue: true,
      array: false,
      step: lastStep - 1,
      color: true,
      expanded: false,
      hidden: !creating || currentStep! < (lastStep - 1),
      edit: {
        editable: creating && currentStep! === (lastStep - 1) ? true : false,
        inputType: InputType.SELECT_SEARCH,
        required: false,
        disabled: serviceInfos.length === 0,
        values: serviceInfos,
        valueToChange: 'service',
      }
    },
    ...(creating ? [expeditionField] : []),
    {
      id: 'user' + (suggestion.id ? `-${suggestion.id}` : ''),
      img: images.suggestions.user,
      title: 'suggestions.data.user',
      value: creating && currentStep === (lastStep - 1) ?
        suggestion.userSuggester : 
        suggestion.userSuggester ? 
          `${suggestion.userSuggester?.firstName} ${suggestion.userSuggester?.lastName}` : 
          suggestionWithoutSuggesterLiteral(suggestion),
      translateValue: true,
      color: true,
      step: lastStep - 1,
      expanded: false,
      hidden: creating && currentStep! < (lastStep - 1),
      edit: {
        editable: !!creating && currentStep! === (lastStep - 1),
        required: true,
        inputType: InputType.INFINITE_SCROLL,
        values: userData && userData.search ? usersSearched : users,
        valueToChange: 'user',
        disabled: suggestion.userSuggester?.firstName === literalService.get('suggestions.anonymousUser', true)
      }
    },
    {
      id: 'type' + (suggestion.id ? `-${suggestion.id}` : ''),
      img: images.suggestions.complaint,
      title: 'suggestions.data.type',
      value: creating && currentStep === lastStep ? suggestion.type : `suggestions.${suggestion.type.toLowerCase()}`,
      translateValue: true,
      array: false,
      hidden: !creating || currentStep! !== lastStep,
      step: lastStep,
      color: true,
      expanded: false,
      edit: {
        editable: creating && currentStep! === lastStep ? true : false,
        required: true,
        inputType: InputType.SELECT_SEARCH,
        values: types,
        valueToChange: 'type',
      }
    },
    {
      id: 'affair' + (suggestion.id ? `-${suggestion.id}` : ''),
      img: images.suggestions.affair,
      title: 'suggestions.data.affair',
      value: creating && currentStep === lastStep && !isObjectLostType && !isObjectFoundType ? suggestion.affair : `suggestions.affairs.${suggestion.affair}`,
      array: false,
      translateValue: true,
      hidden: creating && currentStep! < lastStep,
      step: lastStep,
      color: true,
      expanded: false,
      edit: {
        editable: creating && currentStep! === lastStep && !isObjectLostType && !isObjectFoundType ? true : false,
        required: true,
        inputType: InputType.SELECT_SEARCH,
        values: affairs,
        valueToChange: 'affair',
      }
    },

    {
      id: 'description' + (suggestion.id ? `-${suggestion.id}` : ''),
      img: images.comment,
      title: 'suggestions.comment',
      value: suggestion.description,
      array: false,
      hidden: creating && currentStep! < lastStep,
      step: lastStep,
      color: true,
      placeholder: 'maxChar250',
      tooltipPosition: TooltipPosition.LEFT,
      expanded: false,
      edit: {
        editable: creating && currentStep! === lastStep ? true : false,
        inputType: InputType.LONG_TEXT,
        required: false,
        disabled: false,
        valueToChange: 'description',
        maxLength: 250,
        placeholder: literalService.get('maxChar250', true),
        rows: 4
      }
    },
    {
      id: 'state' + (suggestion.id ? `-${suggestion.id}` : ''),
      img: images.suggestions.status.pending,
      title: 'suggestions.data.status',
      value: ((creating && currentStep === lastStep) || editing) ? suggestion.status : `suggestions.statuses.${suggestion.status!.toLowerCase()}`,
      translateValue: true,
      step: lastStep,
      hidden: creating && currentStep! < lastStep,
      color: true,
      expanded: false,
      edit: {
        editable: true,
        id: suggestion.status,
        inputType: InputType.SELECT_SEARCH,
        translateValue: false,
        required: false,
        disabled: false,
        values: statuses,
        valueToChange: 'status',
      },
    },
    {
      id: 'priority' + (suggestion.id ? `-${suggestion.id}` : ''),
      img: images.arrowUp,
      title: 'suggestions.data.priority',
      value: ((creating && currentStep === lastStep) || editing) ? suggestion.priority : `suggestions.priorities.${suggestion.priority!.toLowerCase()}`,
      translateValue: true,
      array: false,
      hidden: creating && currentStep! < lastStep,
      step: lastStep,
      color: true,
      expanded: false,
      edit: {
        editable: true,
        id: suggestion.priority
          ? suggestion.priority
          : SuggestionPriority.MEDIUM,
        inputType: InputType.SELECT_SEARCH,
        required: true,
        disabled: false,
        values: priorities,
        valueToChange: 'priority',
      },
    },
    {
      id: 'visibility' + (suggestion.id ? `-${suggestion.id}` : ''),
      img: images.visibility,
      title: 'suggestions.data.visibility',
      value: ((creating && currentStep === lastStep) || editing) ? suggestion.visibility : `suggestions.visibilities.${suggestion.visibility}`,
      translateValue: true,
      array: false,
      hidden: creating && currentStep! < lastStep,
      step: lastStep,
      color: true,
      expanded: false,
      edit: {
        editable: true,
        id: suggestion.visibility,
        inputType: InputType.SELECT_SEARCH,
        required: true,
        disabled: false,
        values: visibilities,
        valueToChange: 'visibility',
      },
    },
    {
      id: 'publicMessage' + (suggestion.id ? `-${suggestion.id}` : ''),
      img: images.world,
      title: 'suggestions.data.publicMessage',
      value: suggestion.publicMessage ?? '',
      array: false,
      hidden: !publicMessageRequired || (creating && currentStep! < lastStep),
      step: lastStep,
      color: true,
      tooltipPosition: TooltipPosition.LEFT,
      expanded: false,
      edit: {
        editable: true,
        inputType: InputType.LONG_TEXT,
        required: publicMessageRequired,
        disabled: false,
        valueToChange: 'publicMessage',
        maxLength: 250,
        placeholder: literalService.get('suggestions.data.publicMessagePlaceholder', true),
        rows: 4,
      },
    },
    {
      id: 'clients' + (suggestion.id ? `-${suggestion.id}` : ''),
      img: images.suggestions.clients,
      title: 'suggestions.data.client',
      value: suggestion.client?.name,
      dataToShow: 'code',
      color: true,
      expanded: true,
      hidden: clients.length <= 1 || creating,
      edit: {
        editable: false,
        inputType: InputType.MULTISELECT,
        required: false,
        valueToChange: 'clients'
      },
    },
    {
      id: 'area' + (suggestion.id ? `-${suggestion.id}` : ''),
      img: images.area,
      title: 'suggestions.data.town',
      value: suggestion.town?.name,
      translateValue: true,
      array: false,
      color: true,
      expanded: true,
      hidden: creating,
      edit: {
        editable: false,
        inputType: InputType.SELECT_SEARCH,
        required: true,
        disabled: towns.length === 0,
        values: towns,
        valueToChange: 'town',
      }
    },
    {
      id: 'serviceView' + (suggestion.id ? `-${suggestion.id}` : ''),
      img: images.suggestions.services,
      title: 'suggestions.data.service',
      value: suggestion.service ? (suggestion.service.name ? `${suggestion.service.tag}: ${suggestion.service.name}` : suggestion.service.tag) : '',
      translateValue: true,
      array: false,
      color: true,
      expanded: true,
      hidden: creating,
      edit: {
        editable: false,
        inputType: InputType.SELECT_SEARCH,
        required: false,
        values: serviceInfos,
        valueToChange: 'service',
      }
    },
    ...(creating ? [] : [expeditionField]),
    {
      id: 'internNotes' + (suggestion.id ? `-${suggestion.id}` : ''),
      img: images.suggestions.internNotes,
      title: 'suggestions.data.internNotes',
      value: suggestion.comments ?? '',
      array: false,
      step: lastStep,
      color: true,
      hidden: editing && !suggestion.id || creating,
      tooltipPosition: TooltipPosition.LEFT,
      expanded: true,
      edit: {
        editable: true,
        inputType: InputType.LONG_TEXT,
        required: false,
        disabled: false,
        valueToChange: 'comments',
        maxLength: 250,
        placeholder: literalService.get('maxChar250', true),
        rows: 4,
      },
    },
  ];
  return data;
}

let page = 0;
let usersSearched: any[] = [];
let pageSearched = 0;

function userOption(user: IUserRegular) {
  const extraInfo = user.dni || user.phone || user.email || null;
  const label =
    user.firstName === null && user.lastName === null
      ? `${user.email}`
      : `${user.firstName} ${user.lastName}${extraInfo ? ` (${extraInfo})` : ''}`;
  return { id: user.id, value: user, label };
}

async function loadUsers(
  usersService: UsersService,
  userType: UserType,
  userList: any[],
  pageNumber: number,
  clientId?: number,
  userSelected?: IUser,
  searchValue?: string,
) {
  const response = await usersService.filterByUserType(
    userType,
    UserSortType.ALPHABETICALLY,
    pageNumber,
    10,
    clientId ? [clientId] : [],
    [],
    [],
    [],
    searchValue,
  );
  response.content.forEach((user: IUserRegular) => {
    const status = user.status?.toUpperCase();
    if (status === UserStatus.ACTIVE || status === UserStatus.BLOCKED) userList.push(userOption(user));
  });
  if (userSelected?.id) addSelectedUser(userList, userSelected as IUserRegular);
}

export function addSelectedUser(userList: any[], userSelected: IUserRegular) {
  const existing = userList.find((user: any) => user.id === userSelected.id);
  if (existing) existing.value = userSelected;
  else userList.unshift(userOption(userSelected));
}
